// =============================================================================
// pages/kanban/KanbanPage.tsx — Tela principal do Kanban (rota /kanban).
//
// Arquitetura drag-and-drop:
//   @dnd-kit/core + @dnd-kit/sortable
//   Motivo: acessibilidade out-of-the-box (ARIA live regions, keyboard nav),
//   bundle menor que react-beautiful-dnd (~10KB vs ~28KB gzip),
//   sem deps legadas (React 18 compat nativo), API composable.
//
// Fluxo otimista:
//   1. DragEnd → identifica card e coluna destino.
//   2. Verifica transição válida (ou deixa o backend retornar 422).
//   3. useMoveCard aplica localmente e chama POST /api/kanban/cards/:id/move.
//   4. Erro 422 → rollback + toast com coluna inválida destacada.
//   5. Outros erros → rollback + toast genérico.
//
// LGPD: nenhum dado pessoal nos parâmetros de URL ou logs de console.
// =============================================================================

import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { KanbanCardOverlay } from '../../components/kanban/KanbanCard';
import { KanbanColumn } from '../../components/kanban/KanbanColumn';
import { KanbanDetailModal } from '../../components/kanban/KanbanDetailModal';
import { KanbanFiltersBar } from '../../components/kanban/KanbanFilters';
import { KanbanToastContainer, useKanbanToasts } from '../../components/kanban/KanbanToast';
import { ContextualHelp } from '../../features/help/contextual';
import { useAssignableAgents } from '../../hooks/admin/useAgents';
import { useAppointmentsGate, useLeadNextActions } from '../../hooks/crm/useLeadAppointments';
import type { KanbanCard as KanbanCardType, KanbanFilters } from '../../hooks/kanban/types';
import { useKanbanBoard } from '../../hooks/kanban/useKanbanCards';
import { useKanbanStages } from '../../hooks/kanban/useKanbanStages';
import { useMoveCard } from '../../hooks/kanban/useMoveCard';
import { useCitiesList } from '../../hooks/useCitiesList';

// ── Componente principal ──────────────────────────────────────────────────────

interface KanbanPageProps {
  /** Quando `true`, suprime o cabeçalho interno (h1 + subtitle).
   * Usado ao renderizar o board embutido dentro do CRM. */
  hideHeader?: boolean;
  /**
   * F33-S07 (doc 26 §6.2) — filtros rápidos do CRM aplicados ao board:
   *   - `quickFilterAgentId`: chip "Meus leads" — mesclado no filtro de
   *     agente (sobrepõe o Select manual da barra de filtros do Kanban
   *     enquanto o chip estiver ativo).
   *   - `quickFilterLeadIds`: chips "Retorno hoje"/"Atrasados" — enviados à
   *     API como `lead_ids` (F55-S08: o board é paginado, a interseção no
   *     cliente só veria a página carregada). Conjunto vazio = board vazio.
   *     `undefined` = sem filtro rápido ativo.
   */
  quickFilterAgentId?: string | undefined;
  quickFilterLeadIds?: Set<string> | undefined;
}

/**
 * Página Kanban — board drag-and-drop de leads por etapa.
 *
 * Lib DnD: @dnd-kit/core + @dnd-kit/sortable
 * - Acessibilidade: keyboard DnD, ARIA live regions automáticos.
 * - Bundle: ~10KB gzip, sem legado React 16.
 * - API composable: DragOverlay separado do Sortable.
 *
 * Otimismo UI: useMoveCard aplica mutação local e faz rollback em erro.
 */
export function KanbanPage({
  hideHeader = false,
  quickFilterAgentId,
  quickFilterLeadIds,
}: KanbanPageProps): React.JSX.Element {
  // ── Estado ─────────────────────────────────────────────────────────────────
  const [filters, setFilters] = React.useState<KanbanFilters>({});
  const [activeCard, setActiveCard] = React.useState<KanbanCardType | null>(null);
  const [detailCard, setDetailCard] = React.useState<KanbanCardType | null>(null);
  const [invalidStageId, setInvalidStageId] = React.useState<string | null>(null);

  // ── Data fetching ───────────────────────────────────────────────────────────
  const { stages, isLoading: stagesLoading } = useKanbanStages();
  // Opções reais dos selects de filtro (auditoria N-10: renderizavam vazios).
  // Agentes: o MESMO endpoint do CRM (`agents.id`, escopado por cidade) — o
  // filtro do board compara `leads.agent_id` (F55-S09).
  const { cities } = useCitiesList();
  const { agents } = useAssignableAgents();
  const cityOptions = React.useMemo(
    () => cities.map((c) => ({ id: c.id, name: c.name })),
    [cities],
  );
  const agentOptions = React.useMemo(
    () => agents.map((a) => ({ id: a.id, name: a.display_name })),
    [agents],
  );

  // "Meus leads" (F33-S07) sobrepõe o Select de agente enquanto ativo — não
  // substitui o estado `filters` (o usuário mantém o valor manual ao
  // desativar o chip). "Retorno hoje"/"Atrasados" vão ao SERVIDOR como
  // `lead_ids` (F55-S08): com o board paginado, filtrar no cliente só
  // enxergaria a página carregada e o contador mentiria.
  const effectiveFilters = React.useMemo<KanbanFilters>(
    () => ({
      ...filters,
      ...(quickFilterAgentId ? { agentId: quickFilterAgentId } : {}),
      ...(quickFilterLeadIds ? { leadIds: [...quickFilterLeadIds] } : {}),
    }),
    [filters, quickFilterAgentId, quickFilterLeadIds],
  );
  const stageIds = React.useMemo(() => stages.map((s) => s.id), [stages]);
  const board = useKanbanBoard(stageIds, effectiveFilters);
  const { cardsByStage, isLoading: cardsLoading } = board;

  // ── Próxima ação (F33-S07, doc 26 §6.2) — chip do card, um único GET em
  // lote para todos os leadIds visíveis no board. Gate fechado = mapa vazio,
  // nenhum chip renderiza, board idêntico ao de hoje.
  const appointmentsGate = useAppointmentsGate();
  const visibleLeadIds = React.useMemo(
    () => Object.values(cardsByStage).flatMap((cards) => cards.map((c) => c.leadId)),
    [cardsByStage],
  );
  const { nextActionByLead } = useLeadNextActions(visibleLeadIds, {
    enabled: appointmentsGate.canRead,
  });
  const now = React.useMemo(() => new Date(), []);

  // ── Toasts ─────────────────────────────────────────────────────────────────
  const { toasts, addToast, dismissToast } = useKanbanToasts();

  // ── Move mutation ───────────────────────────────────────────────────────────
  // `board.filters` (normalizados) — precisa ser a MESMA chave usada por
  // useKanbanBoard acima, senão o otimismo/rollback aponta pra uma entrada
  // de cache diferente da que está na tela.
  const moveCard = useMoveCard({
    filters: board.filters,
    onInvalidTransition: (_cardId, targetStageId) => {
      // Destaca a coluna inválida por 2s
      setInvalidStageId(targetStageId);
      setTimeout(() => setInvalidStageId(null), 2_000);

      const stageName = stages.find((s) => s.id === targetStageId)?.name ?? 'essa etapa';
      addToast({
        variant: 'error',
        title: 'Transição inválida',
        description: `Não é possível mover para "${stageName}" a partir do estágio atual.`,
      });
    },
    onError: (_error) => {
      addToast({
        variant: 'warning',
        title: 'Erro ao mover card',
        description: 'Tente novamente. O card foi restaurado à posição original.',
      });
    },
  });

  // ── DnD sensors ────────────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Exige 8px de movimento antes de ativar drag — evita drag acidental em click
        distance: 8,
      },
    }),
  );

  // ── Helpers para encontrar stage de um card ─────────────────────────────────
  const findCardById = React.useCallback(
    (cardId: string): KanbanCardType | undefined => {
      for (const cards of Object.values(cardsByStage)) {
        const found = cards.find((c) => c.id === cardId);
        if (found) return found;
      }
      return undefined;
    },
    [cardsByStage],
  );

  // ── Drag handlers ───────────────────────────────────────────────────────────

  const handleDragStart = React.useCallback(
    (event: DragStartEvent) => {
      const card = findCardById(String(event.active.id));
      if (card) setActiveCard(card);
    },
    [findCardById],
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      setActiveCard(null);
      const { active, over } = event;

      if (!over) return;

      const cardId = String(active.id);
      const card = findCardById(cardId);
      if (!card) return;

      // over.id pode ser o id de um card (sortable) ou um stage (droppable)
      // Nós usamos o stageId de destino: se over.id é um card, pega o stageId
      // dele; se é um stageId direto, usa diretamente.
      let targetStageId = String(over.id);

      // Verifica se o over é um card — nesse caso usa o stageId do card destino
      const overCard = findCardById(targetStageId);
      if (overCard) {
        targetStageId = overCard.stageId;
      }

      // Se já está no mesmo estágio, não faz nada
      if (card.stageId === targetStageId) return;

      // A posição dentro da coluna é a ordem do servidor (prioridade, depois
      // atualização) — o drop só escolhe a coluna.
      moveCard.mutate({ card, targetStageId });
    },
    [findCardById, moveCard],
  );

  // ── Stage name lookup para o modal ─────────────────────────────────────────
  const detailStageName = React.useMemo(
    () => (detailCard ? (stages.find((s) => s.id === detailCard.stageId)?.name ?? '') : ''),
    [detailCard, stages],
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {/* Cabeçalho da página — ocultado quando embutido no CRM */}
      {!hideHeader && (
        <div style={{ animation: 'fade-up var(--dur-slow) var(--ease-out)' }}>
          <div className="flex items-center gap-1">
            <h1
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-3xl)',
                letterSpacing: '-0.045em',
                fontVariationSettings: "'opsz' 48",
                lineHeight: 1,
              }}
            >
              Kanban
            </h1>
            {/* ⓘ tutorial de visualização e movimentação de cards — norma 21 §7 */}
            <ContextualHelp featureKey="crm.kanban.view" className="ml-0.5" />
          </div>
          <p className="font-sans text-sm text-ink-3 mt-1">
            Gerencie leads por etapa com drag-and-drop.
          </p>
        </div>
      )}

      {/* Filtros */}
      <div style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) 0.05s both' }}>
        <KanbanFiltersBar
          filters={filters}
          onChange={setFilters}
          cities={cityOptions}
          agents={agentOptions}
          agentLockedByQuickFilter={quickFilterAgentId !== undefined}
        />
      </div>

      {/* Board
          NÃO aplicamos `animation: fade-up` aqui: o transform do fade-up
          torna este div um containing-block para `position: fixed`, e o
          DragOverlay do dnd-kit (fixed) acaba posicionado relativo a este
          container em vez do viewport — o card "voa pra baixo" no drag. */}
      <div className="flex-1 min-h-0 overflow-x-auto">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          measuring={{
            droppable: { strategy: MeasuringStrategy.Always },
          }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 pb-4 min-w-max h-full">
            {stagesLoading ? (
              // Skeleton de colunas
              <>
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="min-w-[260px] max-w-[300px] rounded-md bg-[var(--bg-elev-2)] animate-pulse"
                    style={{
                      height: '400px',
                      boxShadow: 'var(--elev-1)',
                    }}
                  />
                ))}
              </>
            ) : (
              stages.map((stage) => {
                const cards = cardsByStage[stage.id] ?? [];
                const isOver = activeCard !== null;
                const isInvalid = invalidStageId === stage.id;

                return (
                  <KanbanColumn
                    key={stage.id}
                    stage={stage}
                    cards={cards}
                    total={board.counts?.[stage.id] ?? (board.counts ? 0 : undefined)}
                    hasMore={board.hasMoreByStage[stage.id] ?? false}
                    onLoadMore={() => board.loadMore(stage.id)}
                    isLoading={cardsLoading}
                    isOver={isOver}
                    isInvalid={isInvalid}
                    onCardClick={setDetailCard}
                    nextActionByLead={appointmentsGate.canRead ? nextActionByLead : undefined}
                    now={now}
                  />
                );
              })
            )}
          </div>

          {/* Overlay — clone do card durante drag.
              Renderizado via portal no <body> para escapar de QUALQUER
              ancestor com transform/filter/perspective que possa criar
              containing-block para `position: fixed` e desalinhar o overlay
              do cursor (animations fade-up, etc). */}
          {typeof document !== 'undefined' &&
            createPortal(
              <DragOverlay>
                {activeCard ? <KanbanCardOverlay card={activeCard} /> : null}
              </DragOverlay>,
              document.body,
            )}
        </DndContext>
      </div>

      {/* Modal de detalhe */}
      <KanbanDetailModal
        card={detailCard}
        stageName={detailStageName}
        onClose={() => setDetailCard(null)}
      />

      {/* Toasts */}
      <KanbanToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
